import React from 'react';
import { ActionIcon, Menu, Modal } from '@mantine/core';

import Application from 'types/application';
import { ApplicationUsageStatistics } from 'types/application_usage_statistics';

import { NUMBER_FORMAT } from 'services/number';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useUpdateApplication } from 'application/hooks/useUpdateApplication';
import { useUsageLimits } from 'organizations/hooks/useUsageLimits';
import { useUsageStatistics } from 'organizations/hooks/useUsageStatistics';
import {
  useWorkspacesUtilization,
  WorkspaceUtilization,
} from 'organizations/hooks/useWorkspacesUtilization';
import {
  formatUtilizationValue,
  getEffectiveUtilizationLimit,
  getLowerUtilizationLimit,
} from 'organizations/utils/utilizationLimit';

import { Button } from 'components/Button/Button';
import { CreateApplication } from 'components/CreateApplication/CreateApplication';
import { DeleteApplicationModal } from 'application/components/DeleteApplicationModal/DeleteApplicationModal';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { Loader } from 'components/Loader/Loader';
import { MeterValue } from 'components/Meter';
import { RenameDialog } from 'components/Table/components/TableNameCell/RenameDialog';
import { Text } from 'components/Text/Text';
import { UtilizationMeter } from 'organizations/components/UtilizationMeter/UtilizationMeter';
import {
  WorkspaceAllocatableLimit,
  WorkspaceLimitAllocationDialog,
} from 'organizations/components/WorkspaceLimitAllocationDialog/WorkspaceLimitAllocationDialog';

type WorkspaceLimitColumn = WorkspaceAllocatableLimit & {
  columnTitle: string;
  getUsage: (statistics?: ApplicationUsageStatistics) => number | undefined;
};

// Standard table header weight (see components/Table), uppercase like the
// utilization design.
const HEADER_LABEL_CLASSES =
  'text-xs font-medium uppercase tracking-wider text-[hsl(var(--ui-text))]';

export type WorkspaceLimitsTableProps = {
  canEdit: boolean;
};

/**
 * "Limits by workspace" section of the organization utilization view.
 * Lists per-workspace usage and limits; the Adjust column buttons open the
 * allocation dialog to distribute a limit across workspaces.
 */
export function WorkspaceLimitsTable({ canEdit }: WorkspaceLimitsTableProps) {
  const { data: currentPlan } = useCurrentPlan();
  const { data: usageStatistics } = useUsageStatistics();
  const {
    cloudStorageBytes,
    fileProcessingMinutes,
    monthlyActiveLiveStreams,
    runningStreams,
    recordingRetentionDays,
  } = useUsageLimits();
  const { workspaces, isLoading } = useWorkspacesUtilization();

  const [activeLimit, setActiveLimit] =
    React.useState<WorkspaceAllocatableLimit | null>(null);
  const [isCreatingWorkspace, setIsCreatingWorkspace] = React.useState(false);
  const [deletingWorkspace, setDeletingWorkspace] =
    React.useState<Application | null>(null);
  const [renamingWorkspace, setRenamingWorkspace] =
    React.useState<Application | null>(null);

  const { mutate: updateApplication } = useUpdateApplication();

  function handleRenameSave(name: string) {
    if (!renamingWorkspace) {
      return;
    }

    const application = renamingWorkspace.copy();
    application.name = name;
    updateApplication(application);
    setRenamingWorkspace(null);
  }

  if (currentPlan === undefined) {
    return null;
  }

  const useRunningStreamLimit = Boolean(currentPlan?.use_running_stream_limit);

  const streamColumn: WorkspaceLimitColumn = useRunningStreamLimit
    ? {
        key: 'running_stream_limit',
        name: 'running live streams',
        columnTitle: 'Running live streams',
        limitUnit: 'streams',
        organizationLimit: getEffectiveUtilizationLimit(
          runningStreams.planLimit,
          runningStreams.userLimit
        ),
        getUsage: (statistics) => statistics?.running_stream_count,
      }
    : {
        key: 'monthly_active_live_stream_limit',
        name: 'monthly-active live streams',
        columnTitle: 'Monthly-active live streams',
        limitUnit: 'streams',
        organizationLimit: getEffectiveUtilizationLimit(
          monthlyActiveLiveStreams.planLimit,
          monthlyActiveLiveStreams.userLimit
        ),
        getUsage: (statistics) => statistics?.realtime_stream_count,
      };

  const columns: WorkspaceLimitColumn[] = [
    streamColumn,
    {
      key: 'file_processing_minutes_limit',
      name: 'recorded analytics',
      columnTitle: 'Recorded analytics',
      unit: 'minutes',
      limitUnit: 'minutes',
      organizationLimit: getEffectiveUtilizationLimit(
        fileProcessingMinutes.planLimit,
        fileProcessingMinutes.userLimit
      ),
      getUsage: (statistics) => statistics?.file_processing_minutes,
    },
    ...(currentPlan?.can_retain_recordings
      ? [
          {
            key: 'recording_retention_days',
            name: 'retention',
            columnTitle: 'Retention',
            limitUnit: 'days',
            organizationLimit: getEffectiveUtilizationLimit(
              recordingRetentionDays.planLimit,
              recordingRetentionDays.userLimit
            ),
            isRetention: true,
            getUsage: () => undefined,
          } satisfies WorkspaceLimitColumn,
        ]
      : []),
    {
      key: 'cloud_storage_bytes_limit',
      name: 'cloud storage',
      columnTitle: 'Cloud storage',
      unit: 'filesize',
      limitUnit: 'GiB',
      organizationLimit: getEffectiveUtilizationLimit(
        cloudStorageBytes.planLimit,
        cloudStorageBytes.userLimit
      ),
      getUsage: (statistics) => statistics?.cloud_storage_bytes,
    },
  ];

  const runningDeploymentLimit = Number.isFinite(
    currentPlan?.running_deployment_limit
  )
    ? Number(currentPlan?.running_deployment_limit)
    : null;

  const applicationCount =
    usageStatistics?.application_count ?? workspaces.length;
  const applicationLimit = Number.isFinite(currentPlan?.application_limit)
    ? Number(currentPlan?.application_limit)
    : null;
  const isAtWorkspaceLimit =
    applicationLimit !== null && applicationCount >= applicationLimit;

  return (
    <section className="overflow-hidden rounded-xl border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))]">
      <div className="flex flex-wrap items-start justify-between gap-4 px-6 pb-4 pt-5">
        <div className="min-w-0 flex-1 basis-72">
          <Heading level="3" asChild>
            <h2>Workspace utilization</h2>
          </Heading>
          <ExternalLink href="https://docs.lumeo.com/docs/workspaces">
            Workspace docs
          </ExternalLink>
          <Text className="my-0 mt-1">
            Each workspace can be capped below the org limit, or left uncapped
            to share it.
            {canEdit && <> Use Adjust to distribute a limit across workspaces.</>}
          </Text>
        </div>

        <div className="flex flex-none items-center gap-4">
          <div className="text-right">
            <span className="input-label">Workspaces</span>
            <div className="mt-1 flex justify-end">
              <MeterValue value={applicationCount} max={applicationLimit} />
            </div>
          </div>

          {canEdit && (
            <Button
              variant="primary"
              size="small"
              disabled={isAtWorkspaceLimit}
              disabledTooltip={
                applicationLimit !== null
                  ? `Your plan is limited to ${NUMBER_FORMAT.format(
                      applicationLimit
                    )} workspace${applicationLimit === 1 ? '' : 's'}.`
                  : undefined
              }
              onClick={() => setIsCreatingWorkspace(true)}
            >
              Add workspace
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[56rem] border-collapse">
          <thead>
            <tr className="border-b border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-3))]">
              <th scope="col" className="px-6 py-3 text-left align-top">
                <span className={HEADER_LABEL_CLASSES}>Workspace</span>
              </th>

              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="px-3 py-3 text-left align-top"
                >
                  <div className="flex flex-col items-start gap-1.5">
                    {/* Reserve two lines of title height so the Adjust
                        buttons line up across columns regardless of how many
                        lines each title wraps to. */}
                    <span
                      className={
                        canEdit
                          ? `${HEADER_LABEL_CLASSES} block min-h-8`
                          : HEADER_LABEL_CLASSES
                      }
                    >
                      {column.columnTitle}
                    </span>
                    {canEdit && (
                      <Button
                        variant="secondary"
                        size="xsmall"
                        title={`Adjust ${column.name} limits per workspace`}
                        onClick={() => setActiveLimit(column)}
                      >
                        Adjust
                      </Button>
                    )}
                  </div>
                </th>
              ))}

              <th scope="col" className="px-3 py-3 text-left align-top">
                <span className={HEADER_LABEL_CLASSES}>
                  Running workflow deployments
                </span>
              </th>

              {canEdit && (
                <th scope="col" className="px-3 py-3 pr-4">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {workspaces.map((workspace) => (
              <tr
                key={workspace.application.id}
                className="border-b border-[hsl(var(--ui-4))] last:border-b-0"
              >
                <td className="px-6 py-4 text-sm">
                  <div
                    className="max-w-56 truncate font-semibold text-[hsl(var(--ui-text-highContrast))]"
                    title={workspace.application.name}
                  >
                    {workspace.application.name}
                  </div>
                </td>

                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-3 py-4 text-sm align-top"
                  >
                    <WorkspaceLimitCell column={column} workspace={workspace} />
                  </td>
                ))}

                <td className="px-3 py-4 text-sm align-top">
                  <UsageCell
                    usage={workspace.statistics?.deployment_count}
                    limit={runningDeploymentLimit}
                    isUncapped
                  />
                </td>

                {canEdit && (
                  <td className="px-3 py-3 pr-4 text-right align-middle">
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon
                          aria-label={`Manage ${workspace.application.name}`}
                          variant="subtle"
                        >
                          <Icon name="more-vertical" unstyled />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          onClick={() =>
                            setRenamingWorkspace(workspace.application)
                          }
                        >
                          Rename
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          onClick={() =>
                            setDeletingWorkspace(workspace.application)
                          }
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {isLoading && workspaces.length === 0 && (
          <div className="px-6 py-5">
            <Loader size="small" text="Loading workspaces..." />
          </div>
        )}

        {!isLoading && workspaces.length === 0 && (
          <Text size="small" align="center" className="px-6 py-5">
            No workspaces in this organization.
          </Text>
        )}
      </div>

      <WorkspaceLimitAllocationDialog
        limit={activeLimit}
        workspaces={workspaces}
        onClose={() => setActiveLimit(null)}
      />

      {isCreatingWorkspace && (
        <Modal
          title="Add Workspace"
          opened={isCreatingWorkspace}
          onClose={() => setIsCreatingWorkspace(false)}
        >
          <CreateApplication
            onClose={() => setIsCreatingWorkspace(false)}
            onFinishCreating={() => setIsCreatingWorkspace(false)}
          />
        </Modal>
      )}

      {deletingWorkspace && (
        <DeleteApplicationModal
          opened
          application={deletingWorkspace}
          onClose={() => setDeletingWorkspace(null)}
        />
      )}

      {renamingWorkspace && (
        <RenameDialog
          entity="workspace"
          open
          defaultValue={renamingWorkspace.name}
          onSave={handleRenameSave}
          onCancel={() => setRenamingWorkspace(null)}
        />
      )}
    </section>
  );
}

/**
 * "X / Y" usage cell. Y is the workspace limit, the effective organization
 * limit as fallback, or "unlimited" when neither is set. Uncapped workspaces
 * get an "Uncapped" note; capped ones a red "at cap" note when exhausted.
 */
function UsageCell({
  usage,
  limit,
  unit,
  isUncapped,
}: {
  usage?: number;
  limit: number | null;
  unit?: 'filesize';
  isUncapped?: boolean;
}) {
  const isAtCap =
    !isUncapped &&
    usage !== undefined &&
    limit !== null &&
    usage >= limit;

  return (
    <div className="flex flex-col items-start">
      <div className="whitespace-nowrap">
        <span className="font-semibold text-[hsl(var(--ui-text-highContrast))]">
          {usage !== undefined ? formatUtilizationValue(usage, unit) : '–'}
        </span>{' '}
        <span className="text-[hsl(var(--ui-text))]">
          / {limit !== null ? formatUtilizationValue(limit, unit) : 'unlimited'}
        </span>
      </div>

      {limit !== null && usage !== undefined && (
        <UtilizationMeter
          tone="status"
          value={usage}
          max={limit}
          className="mt-1.5 w-16"
        />
      )}

      {isUncapped && (
        <div className="pt-1 text-xs text-[hsl(var(--ui-text-placeholder))]">
          Uncapped
        </div>
      )}

      {isAtCap && (
        <div className="pt-1 text-xs font-semibold text-[hsl(var(--danger-11))]">
          at cap
        </div>
      )}
    </div>
  );
}

function WorkspaceLimitCell({
  column,
  workspace,
}: {
  column: WorkspaceLimitColumn;
  workspace: WorkspaceUtilization;
}) {
  if (workspace.limits === undefined) {
    return <span className="text-[hsl(var(--ui-text-placeholder))]">–</span>;
  }

  const workspaceLimit = workspace.limits?.[column.key];
  // The workspace cap and org limit can drift out of sync (e.g. the org
  // limit is lowered below an existing workspace cap), so the real
  // effective limit is the lower of the two.
  const effectiveLimit = getLowerUtilizationLimit(
    workspaceLimit,
    column.organizationLimit
  );

  if (column.isRetention) {
    return (
      <div className="flex flex-col items-start">
        {effectiveLimit !== null ? (
          <span className="font-semibold text-[hsl(var(--ui-text-highContrast))]">
            {NUMBER_FORMAT.format(effectiveLimit)} days
          </span>
        ) : (
          <span className="text-[hsl(var(--ui-text-placeholder))]">
            Unlimited
          </span>
        )}

        {!Number.isFinite(workspaceLimit) && (
          <div className="pt-1 text-xs text-[hsl(var(--ui-text-placeholder))]">
            Uncapped
          </div>
        )}
      </div>
    );
  }

  return (
    <UsageCell
      usage={column.getUsage(workspace.statistics)}
      limit={effectiveLimit}
      unit={column.unit === 'filesize' ? 'filesize' : undefined}
      isUncapped={!Number.isFinite(workspaceLimit)}
    />
  );
}
